import type { Dispatch, JSX, SetStateAction } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormLabel,
    Heading,
    Icon,
    IconButton,
    Link,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Text,
} from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Profession } from '@common/enums/profession';
import type { IReduxState } from '../../../configureStore';
import type { IPushHistory } from '../../../utils/history';
import type { IEmployment } from '../../interfaces/employment';
import type { IPatientsHistoryProps, IPatientsListFilters, PatientsListSortKeys } from './Patients';
import type { IPatientInOrganization } from './searchPatient/PatientSearch';
import { FoundPatientStatus } from '../../../../common/enums/foundPatientStatus';
import { translateKey } from '../../../translate';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';
import { FullName, HeaderGroup, Loader } from '../../elements/common';
import { DisplayDatePassed } from '../../elements/DisplayDatePassed';
import { ListPager } from '../../elements/ListPager';
import { NullableStringBirthDate } from '../../elements/NullableStringBirthDate';
import { PatientSearchContext } from './searchPatient/patientSearchContext';
import { findPatientsInOrganization } from './searchPatient/searchPatientFunctions';

interface IPatientListPatients {
    hasMoreItems: boolean;
    page?: number;
    patients: IPatientInOrganization[];
}

interface IPatientsListProps {
    filters: IPatientsListFilters;
    setFilters: Dispatch<SetStateAction<IPatientsListFilters>>;
}

type PatientsListProps = IPushHistory<IPatientsHistoryProps> & IPatientsListProps;

export function PatientsList(props: PatientsListProps): JSX.Element {
    const { limit, sortKey, sortDirection, showOwnPatientsOnly } = props.filters;
    const navigate = useNavigate();

    const abortableFetch = useAbortableFetch();
    const {
        isSearching: isSearchingWithBar,
        patients: patientsFromSearchStartBar,
        patient: foundPatient,
        foundPatientStatus,
    } = useContext(PatientSearchContext);
    const { isKsyos, selectedEmploymentId, organizationId, profession } = useSelector(
        (state: IReduxState) => state.authentication
    );
    const [hasColleagues, setHasColleagues] = useState(false);
    const [isBlurMessageModalVisible, setIsBlurMessageModalVisible] = useState(true);
    const [isSearching, setIsSearching] = useState(true);
    const [isBlurred, setIsBlurred] = useState(true);
    const [isPrivacyModeEnabled, setIsPrivacyModeEnabled] = useState(true);
    const [patients, setPatients] = useState<IPatientListPatients>({ hasMoreItems: false, page: 1, patients: [] });
    const [foundPatients, setFoundPatients] = useState<IPatientListPatients>({ hasMoreItems: false, patients: [] });
    const [page, setPage] = useState(1);

    const isFoundPatientInOrganizationStatus = foundPatientStatus === FoundPatientStatus.foundPatientInOrganization;
    const isNursePractitionerMentalHealth = profession === Profession.nursePractitionerMentalHealth;

    useEffect(() => {
        updateHasColleagues();
        initializePrivacyMode();
    }, []);

    useEffect(() => {
        searchPatientsInOrganization();
    }, [limit, sortKey, sortDirection, showOwnPatientsOnly]);

    useEffect(() => {
        if (!isFoundPatientInOrganizationStatus && page !== patients.page) {
            searchPatientsInOrganization(patients.page);
        }
    }, [isFoundPatientInOrganizationStatus]);

    useEffect(() => {
        if (patientsFromSearchStartBar.length > 0) {
            setFoundPatients({
                hasMoreItems: false,
                patients: patientsFromSearchStartBar as IPatientInOrganization[],
            });
            setPage(1);
        }
    }, [patientsFromSearchStartBar]);

    useEffect(() => {
        handleChangeShowOwnPatientsOnly(showOwnPatientsOnlyByDefault());
    }, [profession]);

    const patientsInList = isFoundPatientInOrganizationStatus ? foundPatients.patients : patients.patients;
    const hasMorePatients = isFoundPatientInOrganizationStatus ? foundPatients.hasMoreItems : patients.hasMoreItems;
    const hasFinishedSearching = !isSearching && !isSearchingWithBar;

    return (
        <>
            <Box
                alignItems="baseline"
                display="flex"
                flexWrap="wrap"
                mb={8}
                mt={10}
            >
                <Heading
                    mr={4}
                    size="large"
                >{`${translateKey('patient.patients')} ${translateKey('practitioner.inYourOrganization')}`}</Heading>
                <Text size="sm">
                    <Link
                        className="automation-patients-list-link-make-readable"
                        onClick={onToggleBlur}
                        variant="button"
                    >
                        {isBlurred ? translateKey('patient.makeReadable') : translateKey('patient.goToPrivateMode')}
                    </Link>
                </Text>
            </Box>

            {hasColleagues && !isKsyos && (
                <Box
                    alignItems="center"
                    display="flex"
                    justifyContent="flex-end"
                >
                    <FormLabel
                        htmlFor="showOwnPatientsOnly"
                        sx={{ mr: 3 }}
                    >
                        {translateKey('patient.displayOwnPatientsOnly')}
                    </FormLabel>
                    <Switch
                        ariaLabel="checkbox"
                        checked={showOwnPatientsOnly}
                        className="automation-patients-list-switch-show-own-patients-only"
                        id="showOwnPatientsOnly"
                        onChange={() => {
                            handleChangeShowOwnPatientsOnly(!showOwnPatientsOnly);
                            setPage(1);
                        }}
                    />
                </Box>
            )}

            <Box position="relative">
                {isBlurMessageModalVisible && patientsInList.length > 0 && (
                    <>
                        <Box
                            height="100%"
                            position="absolute"
                            width="100%"
                            zIndex="1"
                        ></Box>
                        <Box
                            backgroundColor="white"
                            boxShadow="md"
                            p={[6, null, null, 10]}
                            position="absolute"
                            style={{ left: '50%', top: '30%', transform: 'translate(-50%,-50%)' }}
                            width={['90%', '70%', null, '50%']}
                            zIndex="1"
                        >
                            <Box
                                alignItems="flex-start"
                                display="flex"
                                flexDirection="row"
                                justifyContent="space-between"
                                mb={4}
                            >
                                <Text
                                    color="subtle"
                                    fontWeight="bold"
                                    size={['lg', null, null, 'xl']}
                                >
                                    {translateKey('patient.blurHeader')}
                                </Text>
                                <IconButton
                                    icon="close"
                                    labelText="close"
                                    onClick={onClickCloseBlurMessage}
                                    size="small"
                                    variant="filled"
                                ></IconButton>
                            </Box>
                            <Text
                                mb={[8, null, null, 10]}
                                size={['sm', null, null, 'md']}
                            >
                                {translateKey('patient.blurMessage')}
                            </Text>
                            <Box
                                alignItems="center"
                                display="flex"
                                flexDirection={['column', null, null, 'row']}
                                gap={4}
                                justifyContent="flex-end"
                            >
                                <Checkbox
                                    checked={!isPrivacyModeEnabled}
                                    className="automation-patients-list-checkbox-always-make-readable"
                                    label={translateKey('patient.alwaysMakeReadable')}
                                    onChange={(e) => onChangeAlwaysMakeReadable(e.target.checked)}
                                />
                                <Button
                                    className="automation-patients-list-button-make-readable"
                                    data-automation-test="patients-list-button-make-readable"
                                    onClick={onClickMakeReadable}
                                    variant="outline"
                                >
                                    {translateKey('patient.makeReadable')}
                                </Button>
                            </Box>
                        </Box>
                    </>
                )}
                {hasFinishedSearching && patientsInList.length > 0 && (
                    <TableContainer>
                        <Table spacing="medium">
                            <TableHead>
                                <TableRow>
                                    <TableHeader
                                        isSortable={true}
                                        isSortHeader={sortKey === 'nameFamily'}
                                        onClick={() => handleSort('nameFamily')}
                                        sortDirection={sortKey === 'nameFamily' ? sortDirection : undefined}
                                    >
                                        {translateKey('common.name')}
                                    </TableHeader>
                                    <TableHeader>{translateKey('common.gender')}</TableHeader>
                                    <TableHeader
                                        isSortable
                                        isSortHeader={sortKey === 'identifierSocialSecurity'}
                                        onClick={() => handleSort('identifierSocialSecurity')}
                                        sortDirection={
                                            sortKey === 'identifierSocialSecurity' ? sortDirection : undefined
                                        }
                                    >
                                        {translateKey('patient.identifierSocialSecurity')}
                                    </TableHeader>
                                    <TableHeader
                                        isSortable={true}
                                        isSortHeader={sortKey === 'birthDate'}
                                        onClick={() => handleSort('birthDate')}
                                        sortDirection={sortKey === 'birthDate' ? sortDirection : undefined}
                                    >
                                        {translateKey('common.birthDate')}
                                    </TableHeader>
                                    <TableHeader>{translateKey('patient.policyNumber')}</TableHeader>
                                    {isNursePractitionerMentalHealth && (
                                        <TableHeader
                                            isSortable={true}
                                            isSortHeader={sortKey === 'lastNewInfoDateTime'}
                                            onClick={() => handleSort('lastNewInfoDateTime', 'DESC')}
                                            sortDirection={
                                                sortKey === 'lastNewInfoDateTime' ? sortDirection : undefined
                                            }
                                        >
                                            {translateKey('request.lastUpdated')}
                                        </TableHeader>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {patientsInList.map((patient) => (
                                    <TableRow
                                        data-dd-action-name="patients-list-patient"
                                        key={patient.id}
                                        onClick={() => navigate(`/patients/${patient.id}`)}
                                    >
                                        <TableCell
                                            sx={{
                                                filter: isBlurred ? 'blur(5px)' : 'none',
                                            }}
                                        >
                                            <Icon
                                                color="subtle"
                                                icon="patient"
                                                sx={{ mr: 2 }}
                                            />
                                            <FullName {...patient} />
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                filter: isBlurred ? 'blur(5px)' : 'none',
                                            }}
                                        >
                                            {translateKey(`genderEnum.${patient.gender}`)}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                filter: isBlurred ? 'blur(5px)' : 'none',
                                            }}
                                        >
                                            {patient.identifierSocialSecurity}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                filter: isBlurred ? 'blur(5px)' : 'none',
                                            }}
                                        >
                                            <NullableStringBirthDate value={patient.birthDate} />
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                filter: isBlurred ? 'blur(5px)' : 'none',
                                            }}
                                        >
                                            {patient.identifierInsurance}
                                        </TableCell>
                                        {isNursePractitionerMentalHealth && (
                                            <TableCell
                                                sx={{
                                                    filter: isBlurred ? 'blur(5px)' : 'none',
                                                }}
                                            >
                                                <DisplayDatePassed value={patient.lastNewInfoDateTime} />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {!hasFinishedSearching && <Loader horizontalCentered={true} />}
                {!isBlurred && hasFinishedSearching && (
                    <ListPager
                        hasMoreItems={hasMorePatients}
                        isDisabled={isSearching}
                        isForwardOnly={true}
                        itemsPerPage={limit}
                        onChangePage={handleNavigation}
                        page={page}
                        total={patientsInList.length}
                    />
                )}
                {hasFinishedSearching &&
                    patientsInList.length === 0 &&
                    foundPatientStatus === FoundPatientStatus.noSearch && (
                        <HeaderGroup
                            alignment={'center'}
                            header={translateKey('patient.noSearchResultPatientslist')}
                        />
                    )}
            </Box>
        </>
    );

    async function searchPatientsInOrganization(pageToSearch?: number): Promise<void> {
        setIsSearching(true);
        const name =
            foundPatient.nameFamily ?? foundPatient.nameFamily2 ?? foundPatient.nameGiven ?? foundPatient.nameUsual;
        const patientSearchData = {
            birthDate: foundPatient.birthDate,
            identifierSocialSecurity: foundPatient.identifierSocialSecurity,
            name,
        };
        const searchData = isFoundPatientInOrganizationStatus ? patientSearchData : undefined;
        const { hasMoreItems, patients: patientsInOrganization } = await findPatientsInOrganization(
            searchData,
            props.filters,
            pageToSearch ?? page
        );

        if (pageToSearch) {
            setPage(pageToSearch);
        }

        if (isFoundPatientInOrganizationStatus) {
            setFoundPatients({ hasMoreItems, patients: patientsInOrganization });
        } else {
            setPatients({ hasMoreItems, page: pageToSearch ?? page, patients: patientsInOrganization });
        }
        setIsSearching(false);
    }

    async function handleNavigation(pageToSet: number): Promise<void> {
        if (!isFoundPatientInOrganizationStatus) {
            setPatients({ ...patients, page: pageToSet });
        }
        props.pushHistory({ page: pageToSet });
        await searchPatientsInOrganization(pageToSet);
    }

    function handleSort(key: PatientsListSortKeys, defaultSortDirection: 'ASC' | 'DESC' = 'ASC'): void {
        if (sortKey === key) {
            props.setFilters({ ...props.filters, sortDirection: sortDirection === 'DESC' ? 'ASC' : 'DESC' });
        } else {
            props.setFilters({ ...props.filters, sortDirection: defaultSortDirection, sortKey: key });
        }
    }

    async function initializePrivacyMode(): Promise<void> {
        const result = await abortableFetch.fetchJson(`/api/employments/${selectedEmploymentId}`, 'GET');
        const employment = result.json as IEmployment;

        setIsBlurred(employment.isPrivacyModeEnabled);
        setIsPrivacyModeEnabled(employment.isPrivacyModeEnabled);
        setIsBlurMessageModalVisible(employment.isPrivacyModeEnabled);
    }

    async function updatePrivacyMode(isEnabled: boolean): Promise<void> {
        const employment = {
            isPrivacyModeEnabled: isEnabled,
        };
        await abortableFetch.fetchJson(`/api/employments/${selectedEmploymentId}`, 'POST', employment);
    }

    async function onToggleBlur(): Promise<void> {
        if (!isBlurred) {
            await updatePrivacyMode(true);
        }

        setIsBlurred(!isBlurred);
        setIsPrivacyModeEnabled(!isBlurred);
        setIsBlurMessageModalVisible(!isBlurred);
    }

    function onClickCloseBlurMessage(): void {
        setIsBlurMessageModalVisible(false);
    }

    function onChangeAlwaysMakeReadable(alwaysMakeReadable: boolean): void {
        setIsPrivacyModeEnabled(!alwaysMakeReadable);
    }

    async function onClickMakeReadable(): Promise<void> {
        if (!isPrivacyModeEnabled) {
            await updatePrivacyMode(false);
        }
        onToggleBlur();
    }

    async function updateHasColleagues(): Promise<void> {
        const filters = { hasActiveEmployment: true, organizationId };
        const organizationEmployments = (await abortableFetch.fetchJson('/api/practitioners/list', 'POST', { filters }))
            .json.items;
        if (organizationEmployments.length > 1) {
            setHasColleagues(true);
        }
    }

    function showOwnPatientsOnlyByDefault(): boolean {
        return ![
            Profession.bariNurse,
            Profession.dietitian,
            Profession.lifestyleCoach,
            Profession.osasNurse,
            Profession.outpatientAssistant,
            Profession.basicPsychologist,
        ].includes(profession);
    }

    function handleChangeShowOwnPatientsOnly(value: boolean): void {
        props.setFilters({ ...props.filters, showOwnPatientsOnly: value });
    }
}
